import TimeZones from "../../../shared/data/gmtZones.json";
import { KuikaSelectboxOption } from "../../ui-kit/kuika-selectbox/kuika-selectbox";

/**
 * One owner for the timezone map, because it is read in three directions and each reader
 * used to build its own view of it.
 *
 * What is STORED on a job is the IANA id ("Europe/London") — that is what the scheduler
 * consumes, so it never changes here. What is SHOWN is the offset ("GMT+00:00"), which is
 * how the dropdown has always labelled it and, now, how the editor's summary band and the
 * list's Timezone column read it back. A city name is not an answer to "when does this
 * run"; the offset is.
 */

/** The file is written label-to-id: `{ "GMT+03:00": "Europe/Istanbul" }`. */
const TIME_ZONES: Record<string, string> = TimeZones as Record<string, string>;

/**
 * 36 offsets is more than a plain dropdown should ask anyone to scroll, so the list is
 * searchable — by the offset it shows and nothing else. No `searchLabel`: the IANA id is a
 * storage detail the UI no longer names anywhere, and a search that matched "london" would
 * surface a word the user can't see in the result.
 */
export const TIME_ZONE_OPTIONS: KuikaSelectboxOption[] = Object.keys(TIME_ZONES).map(
  (label: string): KuikaSelectboxOption => ({
    value: TIME_ZONES[label],
    label
  })
);

/** The id the pickers default to, and the one every template writes. */
export const DEFAULT_TIME_ZONE: string = TIME_ZONES["GMT+00:00"];

/** Inverted once at module scope — the display path runs id-to-label on every row. */
const TIME_ZONE_LABELS: Record<string, string> = Object.keys(TIME_ZONES).reduce(
  (labels: Record<string, string>, label: string): Record<string, string> => {
    labels[TIME_ZONES[label]] = label;
    return labels;
  },
  {}
);

/**
 * The offset to show for a stored IANA id. Every value this UI can produce is in the map,
 * so the fallback is a safety net for a hand-edited or legacy job, not a path: it hands
 * back whatever was stored rather than swallowing it.
 */
export const describeTimeZone = (timeZone: string | undefined): string | undefined => {
  if (!timeZone) {
    return undefined;
  }
  return TIME_ZONE_LABELS[timeZone] ?? timeZone;
};

/**
 * Minutes from GMT, for sorting. Sorting the label as text puts "GMT+14:00" between
 * "GMT+01:00" and "GMT+02:00", which is why the column sorts on this instead. An unmapped
 * id has no offset to place it by, so it sorts last.
 */
export const timeZoneOffsetMinutes = (timeZone: string | undefined): number => {
  const label: string | undefined = timeZone ? TIME_ZONE_LABELS[timeZone] : undefined;
  if (!label) {
    return Number.MAX_SAFE_INTEGER;
  }
  const match: RegExpMatchArray | null = label.match(/^GMT([+-])(\d{2}):(\d{2})$/);
  if (!match) {
    return Number.MAX_SAFE_INTEGER;
  }
  const sign: number = match[1] === "-" ? -1 : 1;
  return sign * (Number(match[2]) * 60 + Number(match[3]));
};
